import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Tabs as TabsPrimitive } from "radix-ui";

import { cn } from "../../lib/utils";

/**
 * Radix emits `data-state="active" | "inactive"`. Style that attribute — an
 * earlier version of this file used `data-active:`, which Tailwind compiles
 * happily and which never matches, so active tabs rendered with no affordance.
 */
function Tabs({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Root>) {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      className={cn("flex min-h-0 flex-col", className)}
      {...props}
    />
  );
}

const tabsListVariants = cva("group/tabs-list flex items-stretch text-muted-foreground", {
  variants: {
    variant: {
      /** Segmented control on a muted track. */
      default: "h-8 w-fit items-center justify-center gap-0 rounded-lg bg-muted p-[3px]",
      /**
       * Underlined tabs. The container is expected to supply the full-bleed
       * bottom hairline so the active underline sits flush on top of it.
       */
      line: "h-auto w-full justify-start gap-0.5 overflow-x-auto rounded-none bg-transparent p-0",
    },
  },
  defaultVariants: { variant: "default" },
});

function TabsList({
  className,
  variant = "default",
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List> & VariantProps<typeof tabsListVariants>) {
  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      data-variant={variant}
      className={cn(tabsListVariants({ variant }), className)}
      {...props}
    />
  );
}

const tabsTriggerVariants = cva(
  "group/tab inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap font-medium transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default:
          "h-full flex-1 rounded-md border border-transparent px-2.5 text-sm text-foreground/65 hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm",
        line: "h-9 rounded-none border-b-2 border-transparent px-3 text-[0.8125rem] text-muted-foreground hover:bg-muted/40 hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:ring-inset data-[state=active]:border-b-primary data-[state=active]:font-semibold data-[state=active]:text-foreground",
      },
    },
    defaultVariants: { variant: "default" },
  }
);

function TabsTrigger({
  className,
  variant = "default",
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger> &
  VariantProps<typeof tabsTriggerVariants>) {
  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(tabsTriggerVariants({ variant }), className)}
      {...props}
    />
  );
}

/** Count pill for a tab label. Hidden when the count is zero. */
function TabsCount({ children }: { children: number }) {
  if (!children) return null;
  return (
    <span className="inline-flex h-4.5 min-w-5 items-center justify-center rounded-full bg-muted/70 px-1.5 text-[0.6875rem] font-semibold tabular-nums text-muted-foreground group-data-[state=active]/tab:bg-primary/15 group-data-[state=active]/tab:text-primary">
      {children}
    </span>
  );
}

function TabsContent({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn("outline-none", className)}
      {...props}
    />
  );
}

export {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  TabsCount,
  tabsListVariants,
  tabsTriggerVariants,
};
